import React, { useState } from 'react';
import Typography from '@mui/material/Typography';
import { motion, AnimatePresence } from 'framer-motion';
import { fonts } from '../gatsby-theme-material-ui-top-layout/theme';
import styled from 'styled-components';

import Graphic1 from '../img/how-it-works-1.png';
import Graphic2 from '../img/how-it-works-2.png';
import Graphic3 from '../img/how-it-works-3.png';
import Graphic4 from '../img/how-it-works-4.png';

import {
  PROPY_SPACE_GREY_LIGHT,
  PROPY_SPACE_GREY_DARK,
  PROPY_LIGHTER_GREY,
  PROPY_BLACK,
} from '../utils';

import ComponentCarouselContainer from '../containers/ComponentCarouselContainer';

const stepContent = [
  {
    title: "Escrow Opened",
    description: "All it takes simply sending over the purchase contract.",
    icon: Graphic1
  },
  {
    title: "Oracle data collected and AI-processed",
    description: "Propy AI automatically extracts key data from contracts and kicks off multiple automations to save time.",
    icon: Graphic2
  },
  {
    title: "Payment in dollars or crypto",
    description: "Buyers and sellers can transact in the currency of their choosing. Secured by Coinbase.",
    icon: Graphic3
  },
  {
    title: "Ownership settled in the county & onchain",
    description: "Finally a solution for the pervasive fraud plaguing title industry.",
    icon: Graphic4
  }
];

const ZoneContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`

const Zone = styled.div`
  // max-width: 1080px;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const TypographyContainer = styled.div`
  max-width: ${props => props.mobile ? '300px' : '1060px'};
  margin-left: ${props => props.mobile ? 'auto' : 'auto'};
  margin-right: ${props => props.mobile ? 'auto' : 'auto'};
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${props => props.mobile ? 'center' : 'start'};
  width: 100%;
  margin-bottom: ${props => props.mobile ? props.theme.spacing(4) : props.theme.spacing(8)};
`

const TypographySpacer = styled.div`
  margin-top: ${props => props.theme.spacing(1.5)};
`

const StepsOuterContainer = styled.div`
  height: 500px;
  position: relative;
  overflow: hidden;
`

const StepsInnerContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
`

const StepSingle = styled(motion.div)`
  border: 2px solid white;
  padding: ${props => props.theme.spacing(8)};
  background-color: #F9F9F9;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`

const ContentContainer = styled(motion.div)`
  position: relative;
  text-align: left;
`

const StepIcon = styled(motion.div)`
  width: 100px;
  height: 100px;
  margin-top: ${props => props.theme.spacing(2)};
  margin-bottom: ${props => props.theme.spacing(2)};
  background-image: url("${props => props.src}");
  background-color: ${props => props.mobile ? 'transparent' : '#f5f7ff'};
  background-blend-mode: multiply;
  background-repeat: no-repeat;
  background-size: contain;
  position: relative;
`

const AnimatedText = styled(motion.div)`
  position: absolute;
  width: calc(100% - ${props => props.theme.spacing(16)}); // Subtract padding from both sides
  left: ${props => props.theme.spacing(props.selected ? 4 : 8)}; // Add left padding
  top: ${props => props.theme.spacing(8)}; // Add top padding
`

const StepContentLimiter = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 500px;
  align-self: ${props => props.variant === "start" ? "end" : (props.variant === "end" ? "start" : "center")};
  justify-content: space-between;
`

const MobileSlideContainer = styled.div`
  padding-left: ${props => props.theme.spacing(4)};
  padding-right: ${props => props.theme.spacing(4)};
  overflow: hidden;
`

const StepSlide = styled.div`
  box-shadow: 3.24px 2.59px 2.59px 0px rgba(0, 0, 0, 0.02);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  border: 1px solid rgba(240, 238, 238, 1);
  height: 100%;
  text-align: center;
  overflow: hidden;
  padding-left: ${props => props.theme.spacing(2)};
  padding-right: ${props => props.theme.spacing(2)};
`

// const textVariants = {
//   enter: {
//     x: 50,
//     opacity: 0
//   },
//   center: {
//     x: 0,
//     opacity: 1
//   },
//   exit: {
//     x: -50,
//     opacity: 0
//   }
// };

const HowItWorks = ({ 
  isConsideredMobile 
}) => {

  const [activeStep, setActiveStep] = useState(0);

  return (
    <ZoneContainer>
      <Zone>
        <TypographyContainer mobile={isConsideredMobile}>
          <Typography 
            sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_SPACE_GREY_LIGHT, letterSpacing: '0.12em', textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '14px', lineHeight: '14px' }) }}
          >
            HOW DOES IT WORK?
          </Typography>
          <TypographySpacer>
            <Typography 
              variant="h4"
              sx={{ fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}
            >
              Welcome to the future of Closings
            </Typography>
          </TypographySpacer>
        </TypographyContainer>
        {!isConsideredMobile &&
          <StepsOuterContainer>
            <StepsInnerContainer>
              {stepContent.map((step, index) => (
                <StepSingle
                  key={index}
                  onHoverStart={() => setActiveStep(index)}
                  initial={{
                    flex: index > 0 && index < stepContent.length - 1 ? 0.5 : 1,
                    backgroundColor: '#F9F9F9'
                  }}
                  animate={{
                    ...((index > 0 && index < stepContent.length - 1) ? 
                      { flex: activeStep === index ? 2 : 0.4 } : 
                      { flex: activeStep === index ? 2 : 1 }),
                    backgroundColor: activeStep === index ? '#F5F7FF' : '#F9F9F9'
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 300,
                    damping: 30
                  }}
                >
                  <StepContentLimiter
                    variant={
                      index === 0 ? "start" : ((index === (stepContent.length - 1)) ? "end" : "middle")
                    }
                  >
                    <AnimatePresence>
                    {activeStep === index && (
                      <StepIcon 
                        src={step.icon}
                        initial={{ opacity: 0, top: 0 }}
                        animate={{ opacity: 1, top: -20 }}
                        transition={{ delay: 0.3 }}
                        style={{ alignSelf: 'start' }}
                      />
                    )}
                    </AnimatePresence>
                    <AnimatedText
                      selected={index === activeStep}
                      animate={{
                        x: (() => {
                          // First block - always right aligned
                          if (index === 0) {
                            return '0%' // Will be combined with translateX to achieve right alignment
                          }
                          
                          // Last block - left aligned normally, right aligned when active
                          if (index === stepContent.length - 1) {
                            return activeStep === index ? '0%' : '-100%'
                          }
                          
                          // Middle blocks - centered normally, right aligned when active
                          return activeStep === index ? '0%' : '-50%'
                        })(),
                        // Combine both transforms into one translateX
                        transform: (() => {
                          // First block - always right aligned
                          if (index === 0) {
                            return 'translateX(100%)'
                          }
                          
                          // Last block - no transform when left, full transform when active
                          if (index === stepContent.length - 1) {
                            return activeStep === index ? 'translateX(100%)' : 'translateX(0%)'
                          }
                          
                          // Middle blocks - partial transform normally, full transform when active
                          return activeStep === index ? 'translateX(100%)' : 'translateX(0%)'
                        })()
                      }}
                      transition={{ type: "spring", visualDuration: 0.3, bounce: 0 }}
                      style={((activeStep !== index) && ((index > 0) && (index < (stepContent.length - 1)))) ? { textAlign: 'center' } : {}}
                    >
                      <Typography
                        style={{
                          color: activeStep === index ? PROPY_SPACE_GREY_DARK : PROPY_LIGHTER_GREY,
                          letterSpacing: '-0.01em',
                          lineHeight: '43px',
                          fontSize: '40px'
                        }}
                        sx={{ fontFamily: fonts.spaceGrotesk }}
                      >
                        {index + 1}
                      </Typography>
                    </AnimatedText>
                    <AnimatePresence>
                      {activeStep === index && (
                        <ContentContainer
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          // exit={{ opacity: 0, y: 20 }}
                          transition={{ 
                            duration: 0.3,
                            delay: 0.5
                          }}
                          style={{width: '100%'}}
                        >
                          <motion.div
                            initial={{ opacity: 0, y: -30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ 
                              duration: 0.3, 
                              delay: 0.2
                            }}
                          >
                            <Typography 
                              variant="h6" 
                              style={{ color: PROPY_SPACE_GREY_DARK, marginBottom: '16px', fontSize: '40px', lineHeight: '43px', fontWeight: 500, letterSpacing: '-0.01em' }}
                              sx={{ fontFamily: fonts.spaceGrotesk }}
                            >
                              {step.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{ color: PROPY_SPACE_GREY_DARK, marginBottom: '16px', fontSize: '16px', lineHeight: '21px', fontWeight: 300, letterSpacing: '-0.03em' }}
                              sx={{ fontFamily: fonts.robotoMono }}
                            >
                              {step.description}
                            </Typography>
                          </motion.div>
                        </ContentContainer>
                      )}
                    </AnimatePresence>
                  </StepContentLimiter>
                </StepSingle>
              ))}
            </StepsInnerContainer>
          </StepsOuterContainer>
        }
        {isConsideredMobile &&
          <MobileSlideContainer>
            <ComponentCarouselContainer
              autoAnimate={true}
              height={'400px'}
              width={'100%'}
              maxWidth={'500px'}
              components={
                stepContent.map((stepEntry, stepIndex) => (
                  <StepSlide>
                    <StepIcon 
                      mobile={isConsideredMobile}
                      src={stepEntry.icon}
                    />
                    <div>
                      <Typography
                        sx={{ marginBottom: '24px', fontWeight: 300, fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', maxWidth: 900, textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}
                      >
                        {stepIndex + 1}
                      </Typography>
                      <Typography 
                        variant="h4" 
                        sx={{ marginBottom: '24px', fontWeight: 500,fontFamily: fonts.spaceGrotesk, color: PROPY_BLACK, letterSpacing: '-0.01em', maxWidth: 900, textAlign: isConsideredMobile ? 'center' : 'left', ...(isConsideredMobile && { fontSize: '24px', lineHeight: '24px' }) }}
                      >
                        {stepEntry.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        style={{ marginBottom: '32px', fontFamily: fonts.robotoMono, color: PROPY_SPACE_GREY_DARK, fontSize: '14px', lineHeight: '16px', fontWeight: 300, letterSpacing: '-0.03em' }}
                      >
                        {stepEntry.description}
                      </Typography>
                    </div>
                  </StepSlide>
                ))
              }
            />
          </MobileSlideContainer>
        }
      </Zone>
    </ZoneContainer>
  );
};

export default HowItWorks;